import { useEffect, useMemo, useRef, useState } from 'react';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import debounce from 'lodash.debounce';

import { parseCoordinates } from '../mapUtilities';

// Google Places autocomplete: debounced suggestion fetching, session-token
// lifecycle, and resolving a selection (typed coordinates or a picked
// prediction) to a location. Must be used inside an `<APIProvider>`; the Places
// library loads asynchronously, so `ready` may be false on first render.

const DEBOUNCE_TIME = 300;

export type PlaceResolution = {
  location: google.maps.LatLngLiteral;
  formattedAddress?: string;
  isCoordinate: boolean;
};

export type PlacesAutocomplete = {
  ready: boolean;
  suggestions: google.maps.places.AutocompleteSuggestion[];
  fetchSuggestions: (input: string) => void;
  clearSuggestions: () => void;
  resolveSelection: (
    term: string,
    value: string | google.maps.places.AutocompleteSuggestion | null,
  ) => Promise<PlaceResolution | null>;
};

const usePlacesAutocomplete = (): PlacesAutocomplete => {
  const placesLibrary = useMapsLibrary('places');

  const [suggestions, setSuggestions] = useState<
    google.maps.places.AutocompleteSuggestion[]
  >([]);
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  const ensureSessionToken = () => {
    if (placesLibrary && !sessionTokenRef.current) {
      sessionTokenRef.current = new placesLibrary.AutocompleteSessionToken();
    }

    return sessionTokenRef.current;
  };

  const fetchSuggestionsImmediate = async (input: string) => {
    if (!placesLibrary || !input) {
      setSuggestions([]);
      return;
    }

    try {
      const { suggestions: next } =
        await placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions(
          {
            input,
            sessionToken: ensureSessionToken(),
          },
        );
      setSuggestions(next);
    } catch {
      setSuggestions([]);
    }
  };

  const fetchSuggestions = useMemo(
    () => debounce(fetchSuggestionsImmediate, DEBOUNCE_TIME),
    [placesLibrary],
  );

  useEffect(() => () => fetchSuggestions.cancel(), [fetchSuggestions]);

  const resolveSelection = async (
    term: string,
    value: string | google.maps.places.AutocompleteSuggestion | null,
  ): Promise<PlaceResolution | null> => {
    const coordinates = parseCoordinates(term);
    if (coordinates) {
      return { location: coordinates, isCoordinate: true };
    }

    if (!value || typeof value === 'string' || !value.placePrediction) {
      return null;
    }

    const place = value.placePrediction.toPlace();

    try {
      await place.fetchFields({ fields: ['location', 'formattedAddress'] });
    } catch {
      return null;
    }

    sessionTokenRef.current = null;

    if (!place.location) {
      return null;
    }

    return {
      location: { lat: place.location.lat(), lng: place.location.lng() },
      formattedAddress: place.formattedAddress ?? undefined,
      isCoordinate: false,
    };
  };

  return {
    ready: !!placesLibrary,
    suggestions,
    fetchSuggestions,
    clearSuggestions: () => setSuggestions([]),
    resolveSelection,
  };
};

export default usePlacesAutocomplete;
